import axios, { type AxiosResponse, type InternalAxiosRequestConfig } from 'axios';
import { toast } from 'react-hot-toast';
import { useImpersonationStore } from '../stores/impersonationStore';
import { currentOrgHeader } from '../stores/orgContextStore';

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://app.cabos.mondula.com/api';

console.log('🔧 API Configuration:');
console.log('📍 Base URL:', API_BASE_URL);
console.log('🌍 Environment:', import.meta.env.MODE);

// Some services (organizationService, adminService, pssService) call the GLOBAL
// axios instead of this `api` instance — give the default instance the same org
// header so those requests are scoped too.
axios.interceptors.request.use((config) => {
  const orgHeader = currentOrgHeader();
  const url = `${config.baseURL ?? ''}${config.url ?? ''}`;
  if (orgHeader && url.startsWith(API_BASE_URL) && !config.headers['X-Organization-Id']) {
    config.headers['X-Organization-Id'] = orgHeader;
  }
  return config;
});

// Many legacy pages call fetch() directly instead of this axios client. Patch the
// global fetch so API requests also carry the org context (X-Organization-Id) when
// browsing an org (/puma/...) or an org tab in /all — otherwise multi-org and super
// admins get 403s from those pages.
const originalFetch = window.fetch.bind(window);
window.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
  try {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    const orgHeader = currentOrgHeader();
    if (orgHeader && url.startsWith(API_BASE_URL)) {
      const headers = new Headers(
        init?.headers ?? (typeof input === 'object' && 'headers' in input ? (input as Request).headers : undefined)
      );
      if (!headers.has('X-Organization-Id')) headers.set('X-Organization-Id', orgHeader);
      return originalFetch(input, { ...init, headers });
    }
  } catch {
    // fall through to the untouched call
  }
  return originalFetch(input, init);
}) as typeof window.fetch;

declare module 'axios' {
  export interface AxiosRequestConfig {
    /** Skip the generic error toast below; the caller shows its own message (401 refresh still runs). */
    silentErrors?: boolean;
  }
}

export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Axios config that scopes ONE request to an organization, whatever the current mount.
 *
 * The merged /all pages (productions, castings) narrow by `?organization_id=` and send no
 * header, because `forcedOrgId` is null outside <OrgTabs>. A query parameter does not morph
 * the session: `applyOrgContextMorph` only treats a request as an org-admin one when the
 * X-Organization-Id HEADER is there, so the raw multi-org role hits authorize() and 403s —
 * the same page works under /<slug>/… only because the header is set there.
 *
 * When exactly one organization is selected, the request may as well carry the header too:
 * the rows returned are identical (the handler filters by that org either way), so nothing
 * changes for the roles that already work — it only stops relying on a query parameter the
 * handler may ignore. Returns {} when there is no single org (the "All organizations"
 * selection), so callers can spread it unconditionally.
 */
export function orgScoped(orgId?: number | null): { headers?: Record<string, string> } {
  return orgId ? { headers: { 'X-Organization-Id': String(orgId) } } : {};
}

// Request interceptor to add auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Org-scoped URLs (/puma/...) and /all org tabs: send the active org context.
    const orgHeader = currentOrgHeader();
    if (orgHeader && !config.headers['X-Organization-Id']) {
      config.headers['X-Organization-Id'] = orgHeader;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling and token refresh
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        try {
          // Attempt to refresh the token
          const refreshResponse = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refreshToken: refreshToken
          });
          
          const { token, refreshToken: newRefreshToken } = refreshResponse.data.data;
          
          // Update stored tokens
          localStorage.setItem('token', token);
          localStorage.setItem('refreshToken', newRefreshToken);
          
          // Update the original request with new token
          originalRequest.headers.Authorization = `Bearer ${token}`;
          
          // Retry the original request
          return api(originalRequest);
        } catch (refreshError) {
          // Refresh failed — check if we are in an impersonation session
          const impersonationState = useImpersonationStore.getState();
          if (impersonationState.isImpersonating) {
            const orig = impersonationState.originalToken;
            impersonationState.endImpersonation();
            localStorage.removeItem('impersonation-storage');
            if (orig) {
              localStorage.setItem('token', orig);
            } else {
              localStorage.removeItem('token');
            }
            localStorage.removeItem('refreshToken');
            window.location.href = '/organization-admin';
            toast.error('Impersonation session expired. Returning to admin view.');
            return Promise.reject(refreshError);
          }

          // Regular session expiry — clear auth and redirect to login
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('auth-storage');

          // Don't redirect if we're on a completion page
          if (!window.location.pathname.startsWith('/complete-model/')) {
            window.location.href = '/login';
            toast.error('Session expired. Please login again.');
          }
          return Promise.reject(refreshError);
        }
      } else {
        // No refresh token, clear auth and redirect to login (unless on completion page)
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('auth-storage');
        
        // Don't redirect if we're on a completion page
        if (!window.location.pathname.startsWith('/complete-model/')) {
          window.location.href = '/login';
          toast.error('Session expired. Please login again.');
        }
      }
    } else if (originalRequest?.silentErrors) {
      // The caller renders its own message for this request (e.g. "no face found" on face search).
    } else if (error.response?.status >= 500) {
      toast.error('Server error. Please try again later.');
    } else if (error.response?.data?.message) {
      toast.error(error.response.data.message);
    } else {
      toast.error('An unexpected error occurred.');
    }
    
    return Promise.reject(error);
  }
);

export default api; 